import React, { createContext, useContext, useMemo } from 'react';
import type { BlockInteractionResult } from '../../../../services/blockInteractionService';

export interface BlockInteractionContextValue {
    /** Lesson the rendered blocks belong to; null in previews (builder, library). */
    lessonId: number | null;
    /** True when nothing a learner does is recorded (author preview). */
    readOnly: boolean;
    /** Called after a graded attempt so the lesson can track completion. */
    onInteraction?: (blockId: string, result: BlockInteractionResult) => void;
}

const Ctx = createContext<BlockInteractionContextValue>({ lessonId: null, readOnly: true });

/**
 * Tells interactive blocks which lesson they belong to (ported from Scilora
 * BlockInteractionProvider). The default is inert: no lesson, read-only, so a
 * block rendered in a preview shows its content without saving anything.
 */
export const BlockInteractionProvider: React.FC<{ lessonId: number | null; readOnly?: boolean; onInteraction?: BlockInteractionContextValue['onInteraction']; children: React.ReactNode }> = ({ lessonId, readOnly = false, onInteraction, children }) => {
    const value = useMemo(() => ({ lessonId, readOnly, onInteraction }), [lessonId, readOnly, onInteraction]);
    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useBlockInteraction(): BlockInteractionContextValue {
    return useContext(Ctx);
}
